import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Positions & rates (Settings → Hiring → Positions & rates).
//
// CJ 2026-09-11: the advertised barista wage was hardcoded in six places in
// public/apply.html, so a raise meant a code edit and a deploy. This is now
// the only place a rate is written — public/apply.html reads it from
// /api/applications/positions-public, and the hire modal prefills from it.
//
// Gated on manage_hiring_positions (owner / admin / manager).
export function HiringSettingsPositions({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);
  const [drafts, setDrafts] = useState({});

  async function load() {
    setLoading(true);
    try {
      const r = await api('GET', '/api/hiring/positions', null, headers);
      const list = Array.isArray(r?.positions) ? r.positions : [];
      setRows(list);
      setDrafts(Object.fromEntries(list.map(p => [p.id, {
        base_rate: p.base_rate == null ? '' : String(Number(p.base_rate)),
        tip_estimate: p.tip_estimate == null ? '' : String(Number(p.tip_estimate)),
        rate_note: p.rate_note || '',
        active: !!p.active,
      }])));
      setError(null);
    } catch (e) { setError(e.message || String(e)); setRows([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function edit(id, patch) {
    setDrafts(d => ({ ...d, [id]: { ...d[id], ...patch } }));
  }

  function dirty(p) {
    const d = drafts[p.id]; if (!d) return false;
    return d.base_rate !== (p.base_rate == null ? '' : String(Number(p.base_rate)))
      || d.tip_estimate !== (p.tip_estimate == null ? '' : String(Number(p.tip_estimate)))
      || d.rate_note !== (p.rate_note || '')
      || d.active !== !!p.active;
  }

  async function save(p) {
    const d = drafts[p.id];
    setSavingId(p.id);
    try {
      const r = await api('PATCH', `/api/hiring/positions/${p.id}`, {
        base_rate: d.base_rate === '' ? null : Number(d.base_rate),
        tip_estimate: d.tip_estimate === '' ? null : Number(d.tip_estimate),
        rate_note: d.rate_note || null,
        active: !!d.active,
      }, headers);
      if (r?.position) setRows(rs => rs.map(x => (x.id === p.id ? r.position : x)));
      setError(null);
    } catch (e) { setError(e.message || String(e)); }
    finally { setSavingId(null); }
  }

  const money = v => (v == null ? null : `$${Number(v) % 1 === 0 ? Number(v) : Number(v).toFixed(2)}/hr`);
  // The careers headline quotes the first listed position with a base rate,
  // not the lowest — see positions-routes.js.
  const headline = rows.filter(p => p.active && p.base_rate != null)
    .sort((a, b) => (a.sort - b.sort) || String(a.label).localeCompare(String(b.label)))[0] || null;

  const input = { border: '1px solid #d0d4d9', borderRadius: 6, padding: '5px 8px', fontSize: 13, width: '100%' };

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="text-base font-semibold mb-1">Positions &amp; rates</div>
      <div className="text-sm text-gray-500 mb-4">
        These numbers are what the public application page advertises and what the
        hire modal prefills. Changing a rate here takes effect on the careers page
        immediately — no deploy. Leave the base rate blank for positions paid on
        experience and put the wording candidates should see in the note column.
      </div>

      {headline && (
        <div style={{ background: '#fef9ee', border: '1px solid #f5e6c8', borderRadius: 8,
                      padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
          The careers page headline currently reads <strong>starting at {money(headline.base_rate)}</strong>,
          taken from <strong>{headline.label}</strong> — the first listed position with a base rate.
        </div>
      )}

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#a13030',
                      borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-gray-500">No positions configured.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 640 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#666', fontSize: 11, letterSpacing: '0.04em' }}>
                <th style={{ padding: '6px 8px' }}>POSITION</th>
                <th style={{ padding: '6px 8px', width: 120 }}>BASE $/HR</th>
                <th style={{ padding: '6px 8px', width: 120 }}>TIPS $/HR</th>
                <th style={{ padding: '6px 8px' }}>IF NO BASE RATE, SHOW</th>
                <th style={{ padding: '6px 8px', width: 80 }}>LISTED</th>
                <th style={{ padding: '6px 8px', width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(p => {
                const d = drafts[p.id] || {};
                const isDirty = dirty(p);
                return (
                  <tr key={p.id} style={{ borderTop: '1px solid #eceff1' }}>
                    <td style={{ padding: '8px' }}>
                      <span aria-hidden="true" style={{ marginRight: 6 }}>{p.emoji}</span>
                      <strong>{p.label}</strong>
                    </td>
                    <td style={{ padding: '8px' }}>
                      <input type="number" min="0" step="0.25" style={input}
                        value={d.base_rate ?? ''} placeholder="—"
                        onChange={e => edit(p.id, { base_rate: e.target.value })} />
                    </td>
                    <td style={{ padding: '8px' }}>
                      <input type="number" min="0" step="0.25" style={input}
                        value={d.tip_estimate ?? ''} placeholder="—"
                        onChange={e => edit(p.id, { tip_estimate: e.target.value })} />
                    </td>
                    <td style={{ padding: '8px' }}>
                      <input type="text" style={input} placeholder="experience based"
                        value={d.rate_note ?? ''} disabled={d.base_rate !== ''}
                        onChange={e => edit(p.id, { rate_note: e.target.value })} />
                    </td>
                    <td style={{ padding: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!d.active}
                          onChange={e => edit(p.id, { active: e.target.checked })} />
                        <span style={{ fontSize: 12, color: '#666' }}>{d.active ? 'Yes' : 'No'}</span>
                      </label>
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      <button onClick={() => save(p)} disabled={!isDirty || savingId === p.id}
                        style={{ fontSize: 12, padding: '5px 12px', border: 0, borderRadius: 6,
                                  background: isDirty ? 'var(--gold)' : '#e8eaed',
                                  color: isDirty ? '#fff' : '#9aa0a6', fontWeight: 600,
                                  cursor: isDirty ? 'pointer' : 'default' }}>
                        {savingId === p.id ? 'Saving…' : 'Save'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-sm text-gray-500" style={{ marginTop: 12 }}>
        Unlisted positions disappear from the role cards on the application and stop
        counting toward the advertised starting rate. They are not deleted.
      </div>
    </div>
  );
}
